import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Phone, PhoneOff } from 'lucide-react';
import { ApiError, api, fmt, type Capabilities } from '../api';
import { ErrorNote, Field, Note, Spinner } from './ui';

type CallPhase = 'idle' | 'placing' | 'dialing' | 'ringing' | 'in_progress' | 'ended';

const ENDED_TEXT: Record<string, string> = {
  completed: 'Call completed.',
  busy: 'The line was busy.',
  no_answer: 'No answer. The call was not picked up.',
  failed: 'The call could not be connected.',
  cancelled: 'Call cancelled.',
};

/** Where a call is placed, followed and hung up. A demo link has its own. */
export interface CallEndpoints {
  place: string;
  events: (conversationId: string) => string;
  hangup: (conversationId: string) => string;
  /** Whether "View transcript" can open (signed-in accounts only). */
  transcript: boolean;
}

const ACCOUNT_ENDPOINTS: CallEndpoints = {
  place: '/api/calls',
  events: (id) => `/api/conversations/${id}/events`,
  hangup: (id) => `/api/conversations/${id}/hangup`,
  transcript: true,
};

/**
 * "Get a call": the agent rings the visitor's own phone. Status and a live
 * transcript stream in over Server-Sent Events while the call runs.
 */
export default function CallCard({
  agentId,
  caps,
  onUsed,
  endpoints = ACCOUNT_ENDPOINTS,
  footnote,
}: {
  agentId: string;
  caps: Capabilities | null;
  onUsed: () => void;
  endpoints?: CallEndpoints;
  /** Replaces the daily-limit line (a demo link shows its time left instead). */
  footnote?: ReactNode;
}) {
  const prefix = caps?.allowedPrefixes.includes('+91') ? '+91' : '';
  const [number, setNumber] = useState('');
  const [consent, setConsent] = useState(false);
  const [phase, setPhase] = useState<CallPhase>('idle');
  const [endText, setEndText] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [lines, setLines] = useState<{ role: string; text: string }[]>([]);
  const [caption, setCaption] = useState('');
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => () => sourceRef.current?.close(), []);

  function listen(id: string) {
    sourceRef.current?.close();
    const es = new EventSource(endpoints.events(id));
    sourceRef.current = es;
    es.onmessage = (e) => {
      const m = JSON.parse(e.data);
      if (m.type === 'call_status') {
        if (m.status === 'ringing') setPhase('ringing');
        else if (m.status === 'answered' || m.status === 'in_progress') setPhase('in_progress');
        else if (m.ended || ['busy', 'no_answer', 'failed', 'completed', 'cancelled'].includes(m.status)) finish(m.status);
      } else if (m.type === 'state' && m.state !== 'ended') setPhase('in_progress');
      else if (m.type === 'partial') setCaption(m.text);
      else if (m.type === 'transcript' && m.text) {
        setCaption('');
        setLines((l) => (m.replaces ? [...l.slice(0, l.map((x) => x.role).lastIndexOf('assistant')), { role: m.role, text: m.text }] : [...l, { role: m.role, text: m.text }]).slice(-8));
      } else if (m.type === 'ended') finish('completed');
    };
  }

  function finish(status: string) {
    setPhase('ended');
    setEndText(ENDED_TEXT[status] ?? 'Call ended.');
    sourceRef.current?.close();
    sourceRef.current = null;
    onUsed();
  }

  async function call(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLines([]);
    setCaption('');
    setPhase('placing');
    const full = number.trim().startsWith('+') ? number.trim() : `${prefix}${number.trim()}`;
    try {
      const r = await api.post<{ conversationId: string }>(endpoints.place, { agentId, phone: full, consent });
      setConversationId(r.conversationId);
      setPhase('dialing');
      listen(r.conversationId);
      onUsed();
    } catch (err) {
      setError(err);
      setPhase('idle');
    }
  }

  async function hangup() {
    if (!conversationId) return;
    await api.post(endpoints.hangup(conversationId)).catch(() => undefined);
    finish('cancelled');
  }

  const limits = caps?.limits;
  const left = limits ? Math.max(0, limits.dailyCalls - limits.callsUsed) : null;
  const disabled = !caps?.phone;
  const steps: { key: CallPhase; label: string }[] = [
    { key: 'dialing', label: 'Dialing' },
    { key: 'ringing', label: 'Ringing' },
    { key: 'in_progress', label: 'Connected' },
    { key: 'ended', label: 'Ended' },
  ];
  const order = ['placing', 'dialing', 'ringing', 'in_progress', 'ended'];

  return (
    <section className="card" aria-labelledby="call-title">
      <div className="card-head">
        <div>
          <h2 id="call-title">Get a phone call</h2>
          <p>The same agent calls your mobile. Pick up and talk.</p>
        </div>
        <Phone aria-hidden="true" style={{ color: 'var(--accent)', width: 20, height: 20 }} />
      </div>

      {disabled ? (
        <Note tone="warn">{caps?.phoneReason ?? 'Phone calling is not available.'}</Note>
      ) : phase === 'idle' || phase === 'placing' ? (
        <form onSubmit={call}>
          <ErrorNote error={error} />
          <Field label="Your mobile number" htmlFor="phone" hint={error instanceof ApiError && error.code === 'invalid_phone' ? undefined : 'We call this number once, right now. It is not saved in full.'}>
            <div className="phone-input">
              {prefix && !number.startsWith('+') ? <span aria-hidden="true">{prefix}</span> : null}
              <input
                id="phone" type="tel" inputMode="tel" autoComplete="tel-national" required placeholder="98765 43210"
                value={number} onChange={(e) => setNumber(e.target.value.replace(/[^\d+\s-]/g, ''))}
                aria-invalid={error instanceof ApiError && error.code === 'invalid_phone'}
                style={!prefix || number.startsWith('+') ? { borderRadius: 7 } : undefined}
              />
            </div>
          </Field>
          <label className="check" style={{ marginBottom: 14 }}>
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required />
            <span>This is my number, and I agree to receive one AI demo call on it now.</span>
          </label>
          <div className="row between">
            <button className="btn primary" disabled={phase === 'placing' || !consent || !number.trim() || left === 0} data-testid="call-me">
              {phase === 'placing' ? <Spinner label="Placing call" /> : <Phone aria-hidden="true" />} Call me
            </button>
            {footnote ?? (limits ? <span className="small muted">{left} of {limits.dailyCalls} calls left today · up to {fmt.duration(limits.maxCallSeconds * 1000)} each</span> : null)}
          </div>
        </form>
      ) : (
        <div>
          <div className="timeline" aria-label="Call progress">
            {steps.map((s) => (
              <div key={s.key} className={order.indexOf(phase) > order.indexOf(s.key) ? 'done' : phase === s.key ? 'now' : ''}>
                {s.label}
              </div>
            ))}
          </div>
          <p role="status" aria-live="polite" style={{ marginTop: 0 }}>
            {phase === 'dialing' && 'Placing the call to your phone…'}
            {phase === 'ringing' && 'Your phone is ringing. Pick up!'}
            {phase === 'in_progress' && 'Connected. Talk to the agent on your phone.'}
            {phase === 'ended' && endText}
          </p>
          {caption ? <div className="caption"><span className="who">Caller</span>{caption}</div> : null}
          {lines.length ? (
            <div className="transcript" style={{ maxHeight: 220, marginTop: 10 }}>
              {lines.map((l, i) => (
                <div key={i} className={`bubble ${l.role}`}>{l.text}</div>
              ))}
            </div>
          ) : null}
          <div className="row" style={{ marginTop: 12 }}>
            {phase !== 'ended' ? (
              <button className="btn danger" onClick={hangup}><PhoneOff aria-hidden="true" /> Hang up</button>
            ) : (
              <>
                <button className="btn" onClick={() => { setPhase('idle'); setError(null); }}>Call again</button>
                {conversationId && endpoints.transcript ? <Link className="btn ghost" to={`/conversations/${conversationId}`}>View transcript</Link> : null}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
